import type { Vec3 } from '../utils/math';

/**
 * Axis-aligned bounding box in track space:
 *   x = lateral offset from the track centerline (m)
 *   y = height above ground (m)
 *   z = distance along the track (m)
 * All gameplay collision runs in track space, independent of world rotation.
 */
export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export function aabb(): Aabb {
  return { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };
}

export function aabbFromCenterSize(center: Readonly<Vec3>, size: Readonly<Vec3>, out: Aabb = aabb()): Aabb {
  out.minX = center.x - size.x / 2;
  out.maxX = center.x + size.x / 2;
  out.minY = center.y - size.y / 2;
  out.maxY = center.y + size.y / 2;
  out.minZ = center.z - size.z / 2;
  out.maxZ = center.z + size.z / 2;
  return out;
}

export function copyAabb(source: Readonly<Aabb>, out: Aabb): Aabb {
  out.minX = source.minX;
  out.minY = source.minY;
  out.minZ = source.minZ;
  out.maxX = source.maxX;
  out.maxY = source.maxY;
  out.maxZ = source.maxZ;
  return out;
}

export function overlapsX(a: Readonly<Aabb>, b: Readonly<Aabb>): boolean {
  return a.minX < b.maxX && a.maxX > b.minX;
}

export function overlapsY(a: Readonly<Aabb>, b: Readonly<Aabb>): boolean {
  return a.minY < b.maxY && a.maxY > b.minY;
}

export function overlapsZ(a: Readonly<Aabb>, b: Readonly<Aabb>): boolean {
  return a.minZ < b.maxZ && a.maxZ > b.minZ;
}

export function intersects(a: Readonly<Aabb>, b: Readonly<Aabb>): boolean {
  return overlapsX(a, b) && overlapsY(a, b) && overlapsZ(a, b);
}
